'use client';

import { datadogRum } from '@datadog/browser-rum';
import { datadogLogs } from '@datadog/browser-logs';

const DATADOG_CLIENT_TOKEN = 'pubd99897e44e8dca553f55eed6c6187daf';
const DATADOG_APPLICATION_ID = 'e01e4c42-f91f-417d-9608-2d812e48fb02';
const DATADOG_SITE = 'datadoghq.eu';
const DATADOG_SERVICE_NAME = 'sdzv4-frontend';

export interface DatadogRumConfig {
  env: string;
  version: string;
  allowedTracingUrls: string[];
  isVisitor: boolean;
}

interface DatadogSamplingRates {
  sessionSampleRate: number;
  sessionReplaySampleRate: number;
  traceSampleRate: number;
}

const DATADOG_SAMPLING_RATES: {
  bot: DatadogSamplingRates;
  visitor: DatadogSamplingRates;
  nonVisitor: DatadogSamplingRates;
} = {
  bot: {
    sessionSampleRate: 0,
    sessionReplaySampleRate: 0,
    traceSampleRate: 0,
  },
  visitor: {
    sessionSampleRate: 30,
    sessionReplaySampleRate: 2,
    traceSampleRate: 10,
  },
  nonVisitor: {
    sessionSampleRate: 60,
    sessionReplaySampleRate: 30,
    traceSampleRate: 20,
  },
};

function getDatadogSamplingRates({
  isBot,
  isVisitor,
}: {
  isBot: boolean;
  isVisitor: boolean;
}): DatadogSamplingRates {
  if (isBot) {
    return DATADOG_SAMPLING_RATES.bot;
  }

  if (isVisitor) {
    return DATADOG_SAMPLING_RATES.visitor;
  }

  return DATADOG_SAMPLING_RATES.nonVisitor;
}

let isLoaded = false;

export function initializeDatadogRum({
  env,
  version,
  allowedTracingUrls,
  isVisitor,
}: DatadogRumConfig) {
  if (isLoaded) {
    return;
  }

  const botPattern =
    '(googlebot/|bot|Googlebot-Mobile|Googlebot-Image|Google favicon|Mediapartners-Google|bingbot|slurp|java|wget|curl|Commons-HttpClient|Python-urllib|libwww|httpunit|nutch|phpcrawl|msnbot|jyxobot|FAST-WebCrawler|FAST Enterprise Crawler|biglotron|teoma|convera|seekbot|gigablast|exabot|ngbot|ia_archiver|GingerCrawler|webmon |httrack|webcrawler|grub.org|UsineNouvelleCrawler|antibot|netresearchserver|speedy|fluffy|bibnum.bnf|findlink|msrbot|panscient|yacybot|AISearchBot|IOI|ips-agent|tagoobot|MJ12bot|dotbot|woriobot|yanga|buzzbot|mlbot|yandexbot|purebot|Linguee Bot|Voyager|CyberPatrol|voilabot|baiduspider|citeseerxbot|spbot|twengabot|postrank|turnitinbot|scribdbot|page2rss|sitebot|linkdex|Adidxbot|blekkobot|ezooms|dotbot|Mail.RU_Bot|discobot|heritrix|findthatfile|europarchive.org|NerdByNature.Bot|sistrix crawler|ahrefsbot|Aboundex|domaincrawler|wbsearchbot|summify|ccbot|edisterbot|seznambot|ec2linkfinder|gslfbot|aihitbot|intelium_bot|facebookexternalhit|yeti|RetrevoPageAnalyzer|lb-spider|sogou|lssbot|careerbot|wotbox|wocbot|ichiro|DuckDuckBot|lssrocketcrawler|drupact|webcompanycrawler|acoonbot|openindexspider|gnam gnam spider|web-archive-net.com.bot|backlinkcrawler|coccoc|integromedb|content crawler spider|toplistbot|seokicks-robot|it2media-domain-crawler|ip-web-crawler.com|siteexplorer.info|elisabot|proximic|changedetection|blexbot|arabot|WeSEE:Search|niki-bot|CrystalSemanticsBot|rogerbot|360Spider|psbot|InterfaxScanBot|Lipperhey SEO Service|CC Metadata Scaper|g00g1e.net|GrapeshotCrawler|urlappendbot|brainobot|fr-crawler|binlar|SimpleCrawler|Livelapbot|Twitterbot|cXensebot|smtbot|bnf.fr_bot|A6-Indexer|ADmantX|Facebot|Twitterbot|OrangeBot|memorybot|AdvBot|MegaIndex|SemanticScholarBot|ltx71|nerdybot|xovibot|BUbiNG|Qwantify|archive.org_bot|Applebot|TweetmemeBot|crawler4j|findxbot|SemrushBot|yoozBot|lipperhey|y!j-asr|Domain Re-Animator Bot|AddThis)';
  const isBot = new RegExp(botPattern, 'i').test(navigator.userAgent);
  const samplingRates = getDatadogSamplingRates({ isBot, isVisitor });

  datadogLogs.init({
    clientToken: DATADOG_CLIENT_TOKEN,
    site: DATADOG_SITE,
    forwardErrorsToLogs: true,
    sessionSampleRate: samplingRates.sessionSampleRate,
    env,
    service: DATADOG_SERVICE_NAME,
    version,
  });

  datadogRum.init({
    clientToken: DATADOG_CLIENT_TOKEN,
    applicationId: DATADOG_APPLICATION_ID,
    site: DATADOG_SITE,
    service: DATADOG_SERVICE_NAME,
    env,
    version,
    sessionSampleRate: samplingRates.sessionSampleRate,
    sessionReplaySampleRate: samplingRates.sessionReplaySampleRate,
    traceSampleRate: samplingRates.traceSampleRate,
    defaultPrivacyLevel: 'mask',
    trackResources: true,
    trackLongTasks: true,
    trackUserInteractions: true,
    allowedTracingUrls: allowedTracingUrls.map((match) => ({
      match,
      propagatorTypes: ['tracecontext'],
    })),
    // For technical reasons, this configuration is duplicated there
    // /src/SimpleIT/CommonBundle/Resources/views/Oc/main-layout.html.twig
    beforeSend: (event) => {
      if (event.type === 'error') {
        const message = event.error?.message || '';
        const stack = event.error?.stack || '';
        const isNoisyThirdParty = [
          'google',
          'analytics',
          'doubleclick',
          'linkedin',
          'facebook.net',
          'hubspot',
          'hotjar',
        ].some(
          (term) => message.toLowerCase().includes(term) || stack.toLowerCase().includes(term)
        );

        if (isNoisyThirdParty || message === 'Script error.') {
          return false;
        }
      }

      return true;
    },
  });

  isLoaded = true;
}
